import BaseDatePicker from 'react-date-picker';
import { useLocation } from 'react-router-dom';
import DateTimePicker from 'react-datetime-picker';
import { withErrorBoundary } from 'react-error-boundary';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import cs from 'classnames';
import { fromZonedTime } from 'date-fns-tz';
import { addYears, subYears } from 'date-fns';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import DateTimeRangePicker from '@wojtekmaj/react-datetimerange-picker';

import { ia } from 'lib/helpers/utility';
import useTimezone from 'lib/hooks/useTimezone';
import { DEVICE_SIZES } from 'lib/hooks/useWindowSize';
import { useUIContext } from 'lib/context/UIContext/UIContext';

import Icon from '../Icon/Icon';
import Hint from '../shared/Hint';
import ClearIcon from '../shared/ClearIcon';
import { default as Header } from '../shared/Header';
import { formatShortWeekday } from '../Calendar/lib/formatShortWeekday';

import { getDateFormat } from './lib/formatDates';
import { validateDate } from './lib/validateDate';
import { useYearChange } from './hooks/useYearChange';
import { parseDateValue } from './lib/parseDateValue';
import { useCalendarPosition } from './hooks/useCalendarPosition';

import './lib/DateInputPicker.scss';
import 'react-date-picker/dist/DatePicker.css';
import 'react-datetime-picker/dist/DateTimePicker.css';
import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css';
import '@wojtekmaj/react-datetimerange-picker/dist/DateTimeRangePicker.css';

/**
 * @param {import('./lib/propTypes').DatePickerProps} props
 */

const DateInputPicker = (props) => {
  const {
    value,
    onChange = () => {},
    isTime = false,
    isRange = false,
    isTimeRange = false,
    isDateOnly = false,
    isTimezone = isTime || isTimeRange || false,
    isSeconds = false,
    minDate,
    maxDate,
    disabled = false,
    buttonClassName,
    parentClassName,
    isButtonTextWhite,
    isClearable = true,
    isIcon = true,
    iconIsRight = false,
    isCompact = false,
    showCalendarWeekNumbers = false,
    hideCalendar = false,
    openCalendarOnFocus,
    position = 'left',
    error = null,
    ...headerProps
  } = props;

  const shouldOpenOnFocus = openCalendarOnFocus ?? true;

  const calendarRef = useRef(null);
  const wasInsideCalendarRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const isValue = ia(value) ? Boolean(value?.[0]) : Boolean(value);
  const { pathname } = useLocation();
  const isSelfScheduling = pathname.startsWith('/book');

  const timezoneFromHook = useTimezone({ enabled: !isSelfScheduling });
  const timezone = isSelfScheduling
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : timezoneFromHook;

  const [activeStartDate, setActiveStartDate] = useState(() => {
    if (!isValue) return new Date();
    const raw = ia(value) ? value[0] : value;
    return parseDateValue(raw, timezone) || new Date();
  });
  const [activeView, setActiveView] = useState('month');

  const { device } = useUIContext();

  const resolvedMinDate = useMemo(() => minDate ?? subYears(new Date(), 150), [minDate]);
  const resolvedMaxDate = useMemo(() => maxDate ?? addYears(new Date(), 150), [maxDate]);

  const resolvedIsCompact =
    isCompact || device === DEVICE_SIZES.MOBILE || device === DEVICE_SIZES.TABLET_SM;

  if ((isTime || isTimeRange) && !isTimezone)
    throw new Error('isTimezone must be true when isTime or isTimeRange is true.');

  if ((isTime && isTimeRange) || (isRange && isTimeRange) || (isTime && isRange))
    throw new Error('isTime, isRange or isTimeRange cannot be true at the same time.');

  const DatePickerWrapper = isTime
    ? DateTimePicker
    : isRange
      ? DateRangePicker
      : isTimeRange
        ? DateTimeRangePicker
        : BaseDatePicker;

  const onCalendarClose = useCallback(() => {
    setIsOpen(false);
    setActiveStartDate(value ? parseDateValue(value, timezone) || new Date() : new Date());
    setActiveView('month');
  }, [value, timezone]);

  useCalendarPosition({ calendarRef, isOpen, onClose: onCalendarClose, position });

  useYearChange({ value, isRange, isOpen, calendarRef });

  // Close calendar when clicking outside, but allow clicks inside calendar to pass through (for buttons, etc.)
  useEffect(() => {
    if (!isOpen) return undefined;
    const trackClickTarget = (event) => {
      const calendarEl = calendarRef.current;
      const target = 'composedPath' in event ? event.composedPath()[0] : event.target;
      wasInsideCalendarRef.current = !!(calendarEl && target && calendarEl.contains(target));
    };
    document.addEventListener('mousedown', trackClickTarget, true);
    document.addEventListener('touchstart', trackClickTarget, true);
    return () => {
      document.removeEventListener('mousedown', trackClickTarget, true);
      document.removeEventListener('touchstart', trackClickTarget, true);
    };
  }, [isOpen]);

  const shouldCloseCalendar = ({ reason }) => {
    if (reason === 'outsideAction' && wasInsideCalendarRef.current) return false;
    return true;
  };

  const onResetDate = () => {
    onChange(null);
    setActiveStartDate(new Date());
    setActiveView('month');
  };

  const pickerDateToUtc = (pickerDate, tz) => {
    if (!pickerDate) return null;
    return fromZonedTime(pickerDate, tz).toISOString();
  };

  const toDateOnlyString = (date) => {
    if (!date) return null;
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const y = date.getFullYear();
    return `${m}/${d}/${y}`;
  };

  const handleChange = (date) => {
    if (!date) {
      onChange(null);
      setActiveStartDate(new Date());
      return;
    }
    if (isRange || isTimeRange) {
      const [startDate, endDate] = date;
      const finalDate = [pickerDateToUtc(startDate, timezone), pickerDateToUtc(endDate, timezone)];
      onChange(finalDate);
      setActiveStartDate(startDate || new Date());
    } else if (isDateOnly) {
      onChange(toDateOnlyString(date));
      setActiveStartDate(date || new Date());
    } else {
      const finalDate = pickerDateToUtc(date, timezone);
      onChange(finalDate);
      setActiveStartDate(date || new Date());
    }
  };

  const displayValue = (value, timezone) => {
    if (isRange || isTimeRange) {
      if (!value) return [null, null];
      const [startUtc, endUtc] = value;
      return [parseDateValue(startUtc, timezone), parseDateValue(endUtc, timezone)];
    } else {
      if (!value) return null;
      return parseDateValue(value, timezone);
    }
  };

  const pickerValue = useMemo(
    () => displayValue(value, timezone),
    [ia(value) ? `${value?.[0]}|${value?.[1]}` : value, timezone, isRange, isTimeRange]
  );

  return (
    <div
      className={cs(
        'custom-picker w-full',
        parentClassName,
        disabled && 'cursor-not-allowed select-none'
      )}>
      <Header {...headerProps} />
      <DatePickerWrapper
        isOpen={isOpen}
        isCalendarOpen={isOpen}
        onCalendarOpen={() => setIsOpen(true)}
        onCalendarClose={onCalendarClose}
        shouldCloseCalendar={shouldCloseCalendar}
        shouldCloseWidgets={shouldCloseCalendar}
        disableCalendar={hideCalendar && !isIcon}
        calendarProps={{
          inputRef: calendarRef,
          activeStartDate: validateDate(activeStartDate),
          onActiveStartDateChange: ({ activeStartDate }) => setActiveStartDate(activeStartDate),
          view: activeView,
          onViewChange: ({ view }) => setActiveView(view),
          className: cs('mt-2 is-border', resolvedIsCompact && 'is-compact'),
          prev2Label: null,
          next2Label: null,
          prevLabel: <Icon icon="chevron-left-linear" size={16} />,
          nextLabel: <Icon icon="chevron-right-linear" size={16} />,
          formatShortWeekday,
          showWeekNumbers: showCalendarWeekNumbers
        }}
        disabled={disabled}
        format={getDateFormat({ isTime, isTimeRange, isSeconds })}
        calendarIcon={isIcon && <Icon icon="calendar-mono" size={20} />}
        clearIcon={
          <ClearIcon
            isClearable={isClearable ? isValue : false}
            value={value}
            onClick={onResetDate}
            color={isButtonTextWhite ? 'white' : 'neutral'}
            isValue={isValue}
          />
        }
        disableClock
        dayPlaceholder="dd"
        monthPlaceholder="mm"
        yearPlaceholder="yyyy"
        locale="en-US"
        {...props}
        openCalendarOnFocus={shouldOpenOnFocus}
        openWidgetsOnFocus={shouldOpenOnFocus}
        id="date-input-picker"
        className={cs(
          isValue && 'filled',
          !iconIsRight && 'iconIsRight',
          isTimeRange && !isOpen && 'closed',
          pathname === '/iframe.html' && 'isDocumentation',
          isButtonTextWhite && 'white-text',
          buttonClassName
        )}
        minDate={resolvedMinDate}
        maxDate={resolvedMaxDate}
        onChange={(date) => handleChange(date)}
        value={pickerValue}
      />
      <Hint hint={error} error={error} icon={false} />
    </div>
  );
};

export default withErrorBoundary(DateInputPicker);
